import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from 'App/shadcn/lib/utils';
import { Button } from 'App/shadcn/ui/button';
import { Badge } from 'App/shadcn/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from 'App/shadcn/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'App/shadcn/ui/command';

export type EntityOption = { id: number | string; name: string };

type BaseProps = {
	search: (term: string) => Promise<EntityOption[]>;
	placeholder?: string;
	emptyText?: string;
	disabled?: boolean;
	className?: string;
	/**
	 * When true, the trigger reads as plain text with no chrome until hover/focus.
	 * Good for inline summary cards where the form affordance should be implicit.
	 */
	ghost?: boolean;
};

type SingleProps = BaseProps & {
	multiple?: false;
	value: EntityOption | null;
	onChange: (value: EntityOption | null) => void;
};

type MultiProps = BaseProps & {
	multiple: true;
	value: EntityOption[];
	onChange: (value: EntityOption[]) => void;
};

type Props = SingleProps | MultiProps;

const EntityPicker = (props: Props) => {
	const { search, placeholder = 'Search…', emptyText = 'No results', disabled, className, ghost } = props;
	const [open, setOpen] = useState(false);
	const [term, setTerm] = useState('');
	const [results, setResults] = useState<EntityOption[]>([]);
	const [loading, setLoading] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const runSearch = useCallback(
		(q: string) => {
			setLoading(true);
			search(q)
				.then(list => setResults(list ?? []))
				.catch(() => setResults([]))
				.finally(() => setLoading(false));
		},
		[search]
	);

	useEffect(() => {
		if (!open) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => runSearch(term), 200);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [term, open, runSearch]);

	const isSelected = (id: number | string) => {
		if (props.multiple) return props.value.some(v => v.id === id);
		return props.value?.id === id;
	};

	const toggle = (opt: EntityOption) => {
		if (props.multiple) {
			const exists = props.value.some(v => v.id === opt.id);
			props.onChange(exists ? props.value.filter(v => v.id !== opt.id) : [...props.value, opt]);
		} else {
			props.onChange(opt);
			setOpen(false);
		}
	};

	const removeOne = (id: number | string) => {
		if (props.multiple) props.onChange(props.value.filter(v => v.id !== id));
	};

	const summary = (() => {
		if (props.multiple) {
			if (!props.value.length) return <span className="text-muted-foreground">{placeholder}</span>;
			return (
				<div className="flex flex-wrap gap-1">
					{props.value.map(v => (
						<Badge key={v.id} variant="secondary" className="gap-1 text-body-sm">
							{v.name || `#${v.id}`}
							<button
								type="button"
								onClick={e => {
									e.stopPropagation();
									removeOne(v.id);
								}}
								className="opacity-60 hover:opacity-100"
							>
								<X className="h-3 w-3" />
							</button>
						</Badge>
					))}
				</div>
			);
		}
		if (!props.value) return <span className="text-muted-foreground">{placeholder}</span>;
		return <span>{props.value.name || `#${props.value.id}`}</span>;
	})();

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant={ghost ? 'ghost' : 'outline'}
					role="combobox"
					disabled={disabled}
					className={cn(
						'h-auto min-h-8 w-full justify-between gap-2 py-1 text-body-sm font-normal text-left',
						ghost &&
							'border border-transparent bg-transparent px-2 hover:border-input hover:bg-background focus-visible:border-input data-[state=open]:border-input data-[state=open]:bg-background [&_svg.picker-chevron]:opacity-0 hover:[&_svg.picker-chevron]:opacity-100 focus-visible:[&_svg.picker-chevron]:opacity-100 data-[state=open]:[&_svg.picker-chevron]:opacity-100',
						className
					)}
				>
					<span className="flex-1 min-w-0">{summary}</span>
					<ChevronsUpDown className="picker-chevron h-3.5 w-3.5 shrink-0 text-muted-foreground transition-opacity" />
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-[240px] p-0">
				<Command shouldFilter={false}>
					<CommandInput value={term} onValueChange={setTerm} placeholder={placeholder} />
					<CommandList>
						{loading ? (
							<div className="py-6 text-center text-body-sm text-muted-foreground">{'Searching…'}</div>
						) : (
							<>
								<CommandEmpty>{emptyText}</CommandEmpty>
								<CommandGroup>
									{results.map(opt => (
										<CommandItem key={opt.id} value={String(opt.id)} onSelect={() => toggle(opt)}>
											<Check
												className={cn(
													'h-4 w-4',
													isSelected(opt.id) ? 'opacity-100' : 'opacity-0'
												)}
											/>
											<span className="flex-1 truncate">{opt.name || `#${opt.id}`}</span>
										</CommandItem>
									))}
								</CommandGroup>
							</>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};

export default EntityPicker;
